import { ClickAwayListener, Popper } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, isSameDay } from 'date-fns';
import { ComponentProps, FC, useCallback, useRef, useState } from 'react';

import { zIndex } from '../../z-index';
import CalendarIcon from './assets/calendar.svg';
import {
  StyledCalendarContainer,
  StyledCalendarScrollArea,
  StyledDateCalendar,
  StyledRangeContainer,
  StyledTrigger,
  StyledTriggerIcon,
  StyledTriggerText,
} from './DatePicker.styled';
import DatePickerFooter from './DatePickerFooter';
import DatePickerNavHeader from './DatePickerNavHeader';
import { formatWeekday } from './format-weekday';
import { type DateRange, isCompleteRange } from './range-selection';
import { RangeCalendar } from './RangeCalendar';
import SetDatePicker from './SetDatePicker';
import type { DatePickerProps, DatePickerRangeProps, DatePickerSingleProps } from './types';

type View = 'grid' | 'set-date';

/**
 * Keeps a press inside the calendar from reading as an "outside press" to whatever opened it.
 *
 * The popper is portalled to the body, so it is not in the DOM subtree of the panel it was launched
 * from. A panel that dismisses itself by watching the document for presses outside its own subtree
 * — the board's quick-filter popover, for one — therefore closes on the very first day click and
 * takes the calendar down with it. Native listeners in the capture phase, so the event is contained
 * before it reaches the document at all.
 */
const useContainedPresses = () =>
  useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    const contain = (event: Event) => event.stopPropagation();
    const types = ['pointerdown', 'mousedown', 'touchstart'] as const;
    types.forEach((type) => node.addEventListener(type, contain, true));
  }, []);

const SingleDatePicker: FC<DatePickerSingleProps> = ({
  months = 'single',
  value,
  defaultValue,
  onChange,
  placeholder = 'Pick a date',
  disabled,
  error,
  fullWidth,
  dateFormat = 'MMM dd, yyyy',
  changed,
  minDate,
  maxDate,
  ...calendarProps
}) => {
  const initialView: View = months === 'set-date' ? 'set-date' : 'grid';
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<Date | null>(defaultValue ?? null);
  const [draftValue, setDraftValue] = useState<Date | null>(null);
  const [view, setView] = useState<View>(initialView);
  const triggerRef = useRef<HTMLDivElement>(null);
  const containPresses = useContainedPresses();

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const handleOpen = () => {
    if (disabled) return;
    setDraftValue(currentValue ?? null);
    setView(initialView);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setView(initialView);
  };

  const handleToggle = () => {
    if (open) handleClose();
    else handleOpen();
  };

  const commitValue = (date: Date | null) => {
    if (!isControlled) setInternalValue(date);
    onChange?.(date);
  };

  const handleConfirm = () => {
    commitValue(draftValue);
    handleClose();
  };

  const handleSetDateConfirm = (date: Date) => {
    commitValue(date);
    handleClose();
  };

  const displayValue = currentValue ? format(currentValue, dateFormat) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ClickAwayListener onClickAway={handleClose}>
        <div style={{ display: 'inline-block', width: fullWidth ? '100%' : 'auto' }}>
          <StyledTrigger
            ref={triggerRef}
            $fullWidth={fullWidth}
            $open={open}
            $disabled={disabled}
            $error={error}
            data-changed={changed ? 'true' : undefined}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
              if (e.key === 'Escape') handleClose();
            }}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <StyledTriggerIcon>
              <CalendarIcon />
            </StyledTriggerIcon>
            <StyledTriggerText {...(!displayValue && { 'data-placeholder': true })}>
              {displayValue ?? placeholder}
            </StyledTriggerText>
          </StyledTrigger>

          <Popper
            ref={containPresses}
            open={open}
            anchorEl={triggerRef.current}
            placement="bottom-start"
            style={{ zIndex: zIndex.nestedDropdown }}
            modifiers={[
              { name: 'offset', options: { offset: [0, 8] } },
              // Never change alignment (bottom-start -> bottom-end) — that made
              // adjacent triggers (e.g. "From"/"To") open inconsistently.
              {
                name: 'flip',
                options: { fallbackPlacements: ['top-start'], flipVariations: false },
              },
              // But DO shift along the horizontal axis to stay within the actual
              // browser viewport. The app clips viewport overflow silently (no
              // scrollbar), so without this the calendar can render partly or
              // fully off-screen — it must react to the real screen edge, not to
              // the width of whatever narrow container/drawer it's triggered from.
              {
                name: 'preventOverflow',
                options: { altAxis: true, boundary: 'viewport', padding: 8 },
              },
            ]}
          >
            <StyledCalendarScrollArea>
              <StyledCalendarContainer>
                {view === 'grid' ? (
                  <>
                    <StyledDateCalendar
                      value={draftValue}
                      onChange={(date) => setDraftValue(date)}
                      minDate={minDate}
                      maxDate={maxDate}
                      slots={
                        { calendarHeader: DatePickerNavHeader } as unknown as ComponentProps<
                          typeof StyledDateCalendar
                        >['slots']
                      }
                      slotProps={
                        {
                          calendarHeader: { onLabelClick: () => setView('set-date') },
                        } as unknown as ComponentProps<typeof StyledDateCalendar>['slotProps']
                      }
                      dayOfWeekFormatter={formatWeekday}
                      {...calendarProps}
                    />
                    <DatePickerFooter onCancel={handleClose} onConfirm={handleConfirm} />
                  </>
                ) : (
                  <SetDatePicker
                    value={draftValue}
                    minDate={minDate}
                    maxDate={maxDate}
                    onBack={() => setView('grid')}
                    onConfirm={handleSetDateConfirm}
                  />
                )}
              </StyledCalendarContainer>
            </StyledCalendarScrollArea>
          </Popper>
        </div>
      </ClickAwayListener>
    </LocalizationProvider>
  );
};

const EMPTY_RANGE: DateRange = { from: null, to: null };

const formatRange = (range: DateRange, dateFormat: string) => {
  if (!range.from) return null;
  const from = format(range.from, dateFormat);

  if (!range.to) return `${from} – …`;
  if (isSameDay(range.from, range.to)) return from;

  return `${from} – ${format(range.to, dateFormat)}`;
};

const RangeDatePicker: FC<DatePickerRangeProps> = ({
  value,
  defaultValue,
  onChange,
  placeholder = 'Select period',
  disabled,
  error,
  fullWidth,
  dateFormat = 'MMM dd, yyyy',
  changed,
  minDate,
  maxDate,
  ...calendarProps
}) => {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<DateRange>(defaultValue ?? EMPTY_RANGE);
  const [draft, setDraft] = useState<DateRange>(EMPTY_RANGE);
  const triggerRef = useRef<HTMLDivElement>(null);
  const containPresses = useContainedPresses();

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const handleOpen = () => {
    if (disabled) return;
    setDraft(currentValue);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleToggle = () => {
    if (open) handleClose();
    else handleOpen();
  };

  const commitRange = (range: DateRange) => {
    if (!isControlled) setInternalValue(range);
    onChange?.(range);
    handleClose();
  };

  const handleConfirm = () => {
    if (!draft.from) return;
    commitRange(isCompleteRange(draft) ? draft : { from: draft.from, to: draft.from });
  };

  const handleReset = () => {
    setDraft(EMPTY_RANGE);
    commitRange(EMPTY_RANGE);
  };

  const displayValue = formatRange(currentValue, dateFormat);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ClickAwayListener onClickAway={handleClose}>
        <div style={{ display: 'inline-block', width: fullWidth ? '100%' : 'auto' }}>
          <StyledTrigger
            ref={triggerRef}
            $fullWidth={fullWidth}
            $open={open}
            $disabled={disabled}
            $error={error}
            data-changed={changed ? 'true' : undefined}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
              if (e.key === 'Escape') handleClose();
            }}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <StyledTriggerIcon>
              <CalendarIcon />
            </StyledTriggerIcon>
            <StyledTriggerText {...(!displayValue && { 'data-placeholder': true })}>
              {displayValue ?? placeholder}
            </StyledTriggerText>
          </StyledTrigger>

          <Popper
            ref={containPresses}
            open={open}
            anchorEl={triggerRef.current}
            placement="bottom-start"
            style={{ zIndex: zIndex.nestedDropdown }}
            modifiers={[
              { name: 'offset', options: { offset: [0, 8] } },
              // Never change alignment (bottom-start -> bottom-end) — that made
              // adjacent triggers (e.g. "From"/"To") open inconsistently.
              {
                name: 'flip',
                options: { fallbackPlacements: ['top-start'], flipVariations: false },
              },
              // But DO shift along the horizontal axis to stay within the actual
              // browser viewport. The app clips viewport overflow silently (no
              // scrollbar), so without this the calendar can render partly or
              // fully off-screen — it must react to the real screen edge, not to
              // the width of whatever narrow container/drawer it's triggered from.
              {
                name: 'preventOverflow',
                options: { altAxis: true, boundary: 'viewport', padding: 8 },
              },
            ]}
          >
            <StyledCalendarScrollArea>
              <StyledRangeContainer>
                <RangeCalendar
                  range={draft}
                  onRangeChange={setDraft}
                  minDate={minDate}
                  maxDate={maxDate}
                  {...calendarProps}
                />
                <DatePickerFooter
                  onCancel={handleClose}
                  onConfirm={handleConfirm}
                  confirmDisabled={!draft.from}
                  onReset={handleReset}
                  resetDisabled={!currentValue.from && !draft.from}
                />
              </StyledRangeContainer>
            </StyledCalendarScrollArea>
          </Popper>
        </div>
      </ClickAwayListener>
    </LocalizationProvider>
  );
};

const DatePicker: FC<DatePickerProps> = (props) =>
  props.months === 'range' ? <RangeDatePicker {...props} /> : <SingleDatePicker {...props} />;

DatePicker.displayName = 'DatePicker';

export default DatePicker;
export type { DateRange } from './range-selection';
export type {
  DatePickerMonths,
  DatePickerProps,
  DatePickerRangeProps,
  DatePickerSingleProps,
} from './types';
